import {FlightLeg} from '../../schema';
import {RouteVisit} from './route-visits';
import {computeLegWeightsAndCounts} from './util';
import {FlightPax, FlightCgo} from './flight-leg-manager';

export type AssignmentKind = 'pax' | 'cgo';
export interface VisitRange {from: RouteVisit; to: RouteVisit}
export interface Assignment {
    id: string;
    kind: AssignmentKind;
    ranges: VisitRange[];
}
export interface AssignmentRow extends Assignment {
    candidates: VisitRange[];
    status: 'preserved' | 'automatic' | 'review' | 'unassigned';
    reason?: string;
}
export interface AssignmentPlan {
    visits: RouteVisit[];
    rows: AssignmentRow[];
    pax: FlightPax[];
    cargo: FlightCgo[];
}
export const assignmentKey = (assignment: Pick<Assignment, 'kind' | 'id'>) => `${assignment.kind}:${assignment.id}`;
const entityID = (value: any): string => typeof value === 'string' ? value : value?._id;

/** Infer all separate membership ranges, including manual assignments unrelated to scheduled endpoints. */
export function assignmentsFromLegs(legs: FlightLeg[], visits: RouteVisit[]): Assignment[] {
    const assignments = new Map<string, Assignment>();
    (['pax', 'cgo'] as AssignmentKind[]).forEach(kind => {
        const ids = Array.from(new Set(legs.flatMap(leg => (kind === 'pax' ? leg.paxIDs : leg.cgoIDs) || []).map(entityID)));
        ids.forEach(id => {
            const ranges: VisitRange[] = [];
            let start = -1;
            for (let index = 0; index <= legs.length; index++) {
                const onLeg = index < legs.length && ((kind === 'pax' ? legs[index].paxIDs : legs[index].cgoIDs) || []).map(entityID).includes(id);
                if (onLeg && start === -1) start = index;
                if (!onLeg && start !== -1) {
                    ranges.push({from: visits[start], to: visits[index]});
                    start = -1;
                }
            }
            assignments.set(`${kind}:${id}`, {id, kind, ranges});
        });
    });
    return Array.from(assignments.values());
}

export function candidateRanges(visits: RouteVisit[], departureID: string, destinationID: string): VisitRange[] {
    const ranges: VisitRange[] = [];
    visits.forEach((from, i) => {
        if (from.locationID !== departureID) return;
        visits.slice(i + 1).forEach(to => {
            if (to.locationID === destinationID) ranges.push({from, to});
        });
    });
    return ranges;
}

export function rangeIndexes(visits: RouteVisit[], range: VisitRange): [number, number] {
    return [range.from, range.to].map(endpoint => visits.findIndex(visit =>
        visit.key === endpoint?.key && visit.locationID === endpoint?.locationID)) as [number, number];
}

/**
 * Preserves valid visit memberships and resolves unambiguous scheduled journeys.
 *
 * @remarks
 * When an assigned visit disappears, a single remaining scheduled journey can be
 * used automatically. Ambiguous/missing journeys and invalid arbitrary manual
 * ranges require review; explicitly unassigned entities remain unassigned.
 */
export function planAssignments(
    visits: RouteVisit[], pax: FlightPax[], cargo: FlightCgo[], saved: Assignment[] = []
): AssignmentPlan {
    const previous = new Map(saved.map(assignment => [assignmentKey(assignment), assignment]));
    const rows: AssignmentRow[] = [];
    const add = (kind: AssignmentKind, entity: FlightPax | FlightCgo) => {
        const candidates = candidateRanges(visits, entity.departureID, entity.destinationID);
        const old = previous.get(`${kind}:${entity._id}`);
        if (old) {
            const valid = (old.ranges.length > 0 || (old as AssignmentRow).status !== 'review') && old.ranges.every(range => {
                const [start, end] = rangeIndexes(visits, range);
                return start >= 0 && end > start;
            });
            // A removed scheduled endpoint needs no choice when only one journey remains.
            // Keep arbitrary/discontiguous manual memberships subject to explicit review.
            const scheduledJourney =
                old.ranges.length <= 1 &&
                old.ranges.every(
                    (range) =>
                        range.from.locationID === entity.departureID &&
                        range.to.locationID === entity.destinationID
                );
            if (!valid && scheduledJourney && candidates.length === 1) {
                rows.push({
                    ...old,
                    candidates,
                    ranges: candidates,
                    status: 'automatic',
                    reason: undefined,
                });
                return;
            }

            rows.push({...old, candidates, status: valid ? old.ranges.length ? 'preserved' : 'unassigned' : 'review',
                reason: valid ? undefined : 'An assigned visit was removed, moved before its departure, or changed location.'});
        } else {
            rows.push({id: entity._id, kind, candidates, ranges: candidates.length === 1 ? candidates : [],
                status: candidates.length === 1 ? 'automatic' : 'review',
                reason: candidates.length ? 'Choose the departure and arrival visits.' : 'No valid visit pair remains. Reassign or explicitly unassign.'});
        }
    };
    pax.forEach(entity => add('pax', entity));
    cargo.forEach(entity => add('cgo', entity));
    return {visits, rows, pax, cargo};
}

export function chooseAssignment(plan: AssignmentPlan, key: string, candidate: number | 'unassign'): AssignmentPlan {
    return {...plan, rows: plan.rows.map(row => {
        if (assignmentKey(row) !== key) return row;
        if (candidate === 'unassign') return {...row, ranges: [], status: 'unassigned', reason: undefined};
        const range = row.candidates[candidate];
        if (!range) throw new Error('Choose a valid visit pair.');
        return {...row, ranges: [range], status: 'preserved', reason: undefined};
    })};
}

export const needsAssignmentReview = (plan: AssignmentPlan) => plan.rows.some(row => row.status === 'review');

/** The preview may omit unresolved rows; persistence must use the strict default. */
export function legsFromAssignmentPlan(plan: AssignmentPlan, names: Map<string, string>, preview = false): FlightLeg[] {
    if (!preview && needsAssignmentReview(plan)) throw new Error('Review affected assignments before applying the route.');
    const legs = plan.visits.slice(1).map((destination, index) => {
        const assigned = (kind: AssignmentKind) => plan.rows.filter(row => row.kind === kind && row.status !== 'review' && row.ranges.some(range => {
            const [start, end] = rangeIndexes(plan.visits, range);
            return start <= index && end > index;
        })).map(row => row.id);
        const paxIDs = assigned('pax');
        const cgoIDs = assigned('cgo');
        return {order: index, departureID: plan.visits[index].locationID, destinationID: destination.locationID,
            departure: names.get(plan.visits[index].locationID), destination: names.get(destination.locationID),
            paxIDs, cgoIDs, ...computeLegWeightsAndCounts(plan.pax.filter(p => paxIDs.includes(p._id)), plan.cargo.filter(c => cgoIDs.includes(c._id)))};
    });
    attachAssignmentPlan(legs, plan);
    return legs;
}

// A registered symbol survives development module reloads, unlike a module-local WeakMap.
// Non-enumerable metadata stays out of JSON and Object.assign mutation payloads.
const assignmentPlanMetadata = Symbol.for('manifestcentral.flight-leg.assignment-plan');
type PlannedLegs = FlightLeg[] & {[assignmentPlanMetadata]?: AssignmentPlan};

/** Attaches draft-only visit identities and choices without changing the persisted leg contract. */
export function attachAssignmentPlan(legs: FlightLeg[], plan: AssignmentPlan): void {
    Object.defineProperty(legs, assignmentPlanMetadata, {
        value: plan,
        configurable: true,
        writable: true,
        enumerable: false,
    });
}

/** Reads draft metadata, including plans created before a development module reload. */
export function assignmentPlanForLegs(legs: FlightLeg[]): AssignmentPlan | undefined {
    return (legs as PlannedLegs)?.[assignmentPlanMetadata];
}
